import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Container,
  Form,
  Button,
  Table,
  Pagination,
  Row,
  Col,
} from "react-bootstrap";
import Dialog from "../components/Dialog";
import IncomingDialog from "../components/IncomingDialog";
import EditIncomingDialog from "../components/EditIncomingDialog";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import nodeApi from "../lib/api";

function Incoming() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("ic_ch_no");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedChallan, setSelectedChallan] = useState(null);

  const challansPerPage = 10;

  const {
    data: incomingData = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["incoming", filterType, searchTerm, startDate, endDate],
    queryFn: async () => {
      const params = {};
      if (filterType && searchTerm) {
        params.filter_type = filterType;
        params.q = searchTerm;
      }
      const res = await nodeApi.get("/incoming", { params });
      return res.data;
    },
    onError: (err) => {
      toast.error(`Failed to fetch challans: ${err.message}`);
    },
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [incomingData, searchTerm, filterType, startDate, endDate]);

  const filteredChallans = incomingData.filter((challan) => {
    if (!startDate && !endDate) return true;
    const rawDate = challan.ic_ch_date;
    if (!rawDate) return false;

    const challanDateStr = new Date(rawDate).toISOString().split("T")[0];
    const startStr = startDate
      ? new Date(startDate).toISOString().split("T")[0]
      : null;
    const endStr = endDate
      ? new Date(endDate).toISOString().split("T")[0]
      : null;

    if (startStr && challanDateStr < startStr) return false;
    if (endStr && challanDateStr > endStr) return false;
    return true;
  });

  const sortedChallans = [...filteredChallans].sort((a, b) => {
    const dateA = new Date(a.updated_at);
    const dateB = new Date(b.updated_at);
    if (isNaN(dateA)) return 1;
    if (isNaN(dateB)) return -1;
    return dateB - dateA;
  });

  const handleViewChallan = (challan) => {
    setSelectedChallan(challan);
    setShowDialog(true);
  };

  const handleEditChallan = (challan) => {
    setSelectedChallan(challan);
    setShowEditDialog(true);
  };

  const indexOfLast = currentPage * challansPerPage;
  const indexOfFirst = indexOfLast - challansPerPage;
  const currentChallans = sortedChallans.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedChallans.length / challansPerPage);
  const pages = [];
  if (totalPages > 1) {
    pages.push(1);
    if (currentPage > 3) pages.push("dots-left");
    for (let p = currentPage - 1; p <= currentPage + 1; p++) {
      if (p > 1 && p < totalPages) pages.push(p);
    }
    if (currentPage < totalPages - 2) pages.push("dots-right");
    if (totalPages > 1) pages.push(totalPages);
  }

  return (
    <div className="d-flex flex-column vh-100">
      <Navbar />
      <div className="d-flex flex-grow-1 overflow-hidden">
        <Sidebar />
        <Container
          fluid
          className="flex-grow-1 d-flex flex-column p-4 overflow-hidden"
        >
          <h4 className="mb-3 fw-bold">Incoming Challans</h4>

          {/* Search & Filter Controls */}
          <Row className="mb-3 align-items-end">
            <Col xs={4} md={2}>
              <Form.Group>
                <Form.Label className="small mb-1">Filter By</Form.Label>
                <Form.Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="ic_ch_no">Challan Number</option>
                  <option value="ic_source_name">Source Name</option>
                  <option value="ic_destination_name">Destination Name</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={8} md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Search</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small mb-1">Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small mb-1">End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              {(startDate || endDate || searchTerm) && (
                <Button
                  variant="outline-danger"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setSearchTerm("");
                    setFilterType("ic_ch_no");
                  }}
                >
                  Clear
                </Button>
              )}
            </Col>
            <Col xs={6} md={1} className="ms-auto">
              <Button variant="primary" onClick={() => setShowAddDialog(true)}>
                + Add
              </Button>
            </Col>
          </Row>
          <style>
            {`
            .col-challan{ width: 220px;}
            .col-from{ width: 250px;}
            .col-source{ width: 200px;}
            .col-destination{ width: 250px;}
            .col-date{ width: 250px;}
            .col-actions{ width: 200px;}
      
            `}
          </style>

          {/* Table */}
          <div className="table-responsive flex-grow-1 overflow-auto">
            <Table bordered hover className="mb-0">
              <thead className="table-secondary sticky-top">
                <tr>
                  <th className="col-challan" >Incoming Challan No</th>
                  <th className="col-from" >From</th>
                  <th className="col-source" >Source Name</th>
                  <th className="col-destination" >Destination Name</th>
                  <th className="col-date" >Date</th>
                  <th className="col-actions" >Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : currentChallans.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">
                      {isError
                        ? "Error loading challans"
                        : "No matching challans found"}
                    </td>
                  </tr>
                ) : (
                  currentChallans.map((challan) => (
                    <tr key={challan._id}>
                      <td>{challan.ic_ch_no}</td>
                      <td>{challan.ic_from}</td>
                      <td>{challan.ic_source_name}</td>
                      <td>{challan.ic_destination_name}</td>
                      <td>
                        {new Date(challan.ic_ch_date).toLocaleDateString(
                          "en-GB"
                        )}
                      </td>
                      <td>
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2"
                          title="Edit"
                          onClick={() => handleEditChallan(challan)}
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          title="View Details"
                          onClick={() => handleViewChallan(challan)}
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              className="justify-content-center mt-3"
              style={{ position: "sticky", bottom: 0, zIndex: 10 }}
            >
              <Pagination.Prev
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </Pagination.Prev>
              {pages.map((p, idx) => (
                <Pagination.Item
                  key={idx}
                  active={p === currentPage}
                  disabled={p === "dots-left" || p === "dots-right"}
                  onClick={() =>
                    p !== "dots-left" && p !== "dots-right" && setCurrentPage(p)
                  }
                >
                  {p === "dots-left" || p === "dots-right" ? "…" : p}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Pagination.Next>
            </Pagination>
          )}
        </Container>
      </div>

      {/* View Dialog */}
      <Dialog
        show={showDialog}
        onClose={() => setShowDialog(false)}
        challan={selectedChallan}
        type="incoming"
      />

      {/* Add Challan Modal */}
      <IncomingDialog
        show={showAddDialog}
        onClose={() => setShowAddDialog(false)}
      />

      {/* Edit Challan Modal */}
      <EditIncomingDialog
        show={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        challan={selectedChallan}
      />
    </div>
  );
}

export default Incoming;
